// src/pages/FAQ.js
import React from 'react';

function FAQ() {
  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">CTE Indicator FAQ</h1>
      
      <div className="bg-blue-50 p-4 rounded mb-6 border border-blue-200">
        <h2 className="text-lg font-semibold mb-2">About This Page</h2>
        <p>
          This FAQ provides information about Career and Technical Education (CTE) Indicators, 
          how they're calculated, and how to interpret them. The information is based on the 
          Texas Education Agency (TEA) CTE Indicator Auto-Code guidance.
        </p>
      </div>
      
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-4">CTE Indicator Codes</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-100 p-4 rounded border">
            <h3 className="font-semibold text-gray-700 mb-1">Code 4: Not CTE</h3>
            <p className="text-sm">
              A student who never completed any high school CTE courses.
            </p>
          </div>
          
          <div className="bg-orange-100 p-4 rounded border">
            <h3 className="font-semibold text-orange-700 mb-1">Code 5: CTE Participant</h3>
            <p className="text-sm">
              A student who completes either one high school CTE course for any number of credits or 
              more than one high school CTE course for less than two credits.
              (The student does not have to pass or receive credit.)
            </p>
          </div>
          
          <div className="bg-purple-100 p-4 rounded border">
            <h3 className="font-semibold text-purple-700 mb-1">Code E: CTE Explorer</h3>
            <p className="text-sm">
              A student who completes two or more high school CTE courses for a total of two or more 
              credits and is not a Participant, Concentrator, or Completer. 
              (The student does not have to pass or receive credit.)
              <br /><br />
              <span className="italic">OR</span>
              <br /><br />
              A student who completes, passes, and receives enough credits to be coded as a 
              Concentrator or Completer in a Regional Program of Study, but completes the year 
              in an unapproved region.
            </p>
          </div>
          
          <div className="bg-blue-100 p-4 rounded border">
            <h3 className="font-semibold text-blue-700 mb-1">Code 6: CTE Concentrator</h3>
            <p className="text-sm">
              A student who completes and passes two or more high school CTE courses for a 
              total of at least two credits within the same Program of Study and is not a Completer.
            </p>
          </div>
          
          <div className="bg-green-100 p-4 rounded border">
            <h3 className="font-semibold text-green-700 mb-1">Code 7: CTE Completer</h3>
            <p className="text-sm">
              A student who completes and passes three or more high school CTE courses for a 
              total of four or more credits, including at least one level three or level four course, 
              from within the same Program of Study.
            </p>
          </div>
        </div>
        
        <div className="bg-yellow-50 p-4 rounded border mb-6">
          <p className="text-sm italic">
            <strong>Note:</strong> Regional programs of study may be approved in ESC geographic 
            regions where there is specialized regional labor market demand for specific occupations. 
            Students who complete the school year in an unapproved region and are a code 6 (Concentrator) 
            or code 7 (Completer) in a regional program of study outside of approved geographic ESC 
            regions are reassigned as a code E (Explorer).
          </p>
        </div>
      </div>
      
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-4">Frequently Asked Questions</h2>
        
        <div className="space-y-4">
          <div className="border rounded p-4">
            <h3 className="font-semibold">
              Why was the self-reported CTE indicator changed to auto-coded by the TEA?
            </h3>
            <p className="mt-2">
              The Strengthening Career and Technical Education for the 21st Century Act (Perkins V) 
              requires changes to how career and technical education (CTE) students are reported. 
              At the time of Perkins reauthorization, the Texas Education Agency (TEA) evaluated the 
              method of reporting CTE students and determined there was an opportunity to improve data 
              quality by calculating the indicators with existing data reported by districts. Auto-coded 
              data also reduces the district reporting burden by removing the district self-reported CTE 
              indicator data element.
            </p>
          </div>
          
          <div className="border rounded p-4">
            <h3 className="font-semibold">
              What are the benefits of Auto-Coding CTE indicators?
            </h3>
            <p className="mt-2">
              The TEA reduced the district reporting burden by removing the self-reported CTE indicators. 
              Previously most districts had to use decision charts to manually evaluate student course 
              completions and a student's 4-year plan of study to determine a CTE indicator. The process 
              was found to be inconsistently applied at the local level and some students were not being 
              coded correctly. Auto-coding applies the business rules to all districts the same way each 
              year, so there is less interpretation of how to code students.
            </p>
          </div>
          
          <div className="border rounded p-4">
            <h3 className="font-semibold">
              Why does a student not have to pass a high-school CTE course to be a code 5 Participant or code E Explorer?
            </h3>
            <p className="mt-2">
              The TEA wanted to provide districts with credit for students attempting and completing a CTE 
              course, even if they did not pass the course.
            </p>
          </div>
          
          <div className="border rounded p-4">
            <h3 className="font-semibold">
              What is the purpose of the new code E Explorer CTE indicator?
            </h3>
            <p className="mt-2">
              When students complete CTE courses across multiple programs of study and do not concentrate 
              their CTE courses in one area, they are "exploring" career and technical education. The TEA 
              wanted to provide districts with a way to identify students who are taking a broader approach 
              to CTE, in order to make local program improvements to concentrate or focus student CTE 
              experiences in the future.
            </p>
          </div>
          
          <div className="border rounded p-4">
            <h3 className="font-semibold">
              Why are so many of my students assigned multiple CTE indicators of code 6 and code 7?
            </h3>
            <p className="mt-2">
              At the time of the new system rollout several of the programs of study lacked approved CTE 
              courses that make them unique and different from other program of study. This "overlapping" 
              of courses across two or more programs of study is accurate and temporary, until more unique 
              courses are added in specific programs of study. Each year during and after the rollout, TEA 
              is working with the State Board of Education (SBOE) to add CTE courses in the programs of study.
            </p>
          </div>
          
          <div className="border rounded p-4">
            <h3 className="font-semibold">
              Our district has a lot of code 5s (Participants) and not enough codes 6 or 7. What does that mean?
            </h3>
            <p className="mt-2">
              A high frequency of code 5 (Participant) CTE indicators means students are not completing enough 
              CTE courses in approved programs of study to earn a higher code (codes E, 6 or 7). Districts 
              should evaluate course completion records and compare them to the approved CTE programs of study 
              to determine if changes to local CTE course offerings can increase the number of code 6 (Concentrator) 
              or code 7 (Completer) CTE indicators.
            </p>
          </div>
          
          <div className="border rounded p-4">
            <h3 className="font-semibold">
              Our district has a lot of code Es (Explorers) and not enough codes 6 or 7. What does that mean?
            </h3>
            <p className="mt-2">
              A high frequency of code E (Explorer) CTE indicators means students are not completing enough CTE 
              courses in approved programs of study to earn a higher code (codes 6 or 7). Districts should 
              evaluate course completion records and compare them to the approved CTE programs of study to 
              determine if changes to local CTE course offerings can increase the number of code 6 (Concentrator) 
              or code 7 (Completer) CTE indicators.
            </p>
          </div>
          
          <div className="border rounded p-4">
            <h3 className="font-semibold">
              Why was a student coded an Explorer if they completed a regional program of study?
            </h3>
            <p className="mt-2">
              Students completing a regional program of study in unapproved regions will have their code 6 or 
              code 7 converted to a code E when they complete a school year in an unapproved region. Districts 
              can offer any of the programs of study, including regional programs of study that are not approved 
              in the district's ESC geographical region. As students move from one ESC region to another, their 
              course completion history follows them each year when the auto-code is calculated in the fall.
            </p>
          </div>
          
          <div className="border rounded p-4">
            <h3 className="font-semibold">
              What are regional programs of study?
            </h3>
            <p className="mt-2">
              Regional programs of study are specialized programs and occupations that are in demand only in 
              select geographic regions of the state. Regional programs of study are approved for a four-year 
              period in specific geographic regions. Districts can apply for a regional program of study through 
              their geographic education service center.
            </p>
          </div>
          
          <div className="border rounded p-4">
            <h3 className="font-semibold">
              Can a student be a concentrator or completer in more than one program of study?
            </h3>
            <p className="mt-2">
              Yes. Students completing and passing CTE courses identified in multiple programs of study are 
              assigned a CTE indicator for each program of study based on the business rules.
            </p>
          </div>
        </div>
      </div>
      
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-4">Understanding CTE Completer Requirements</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse mb-4">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-4 py-2">Requirement</th>
                <th className="border px-4 py-2">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border px-4 py-2 font-semibold">Courses</td>
                <td className="border px-4 py-2">3 or more courses in the same program of study</td>
              </tr>
              <tr>
                <td className="border px-4 py-2 font-semibold">Credits</td>
                <td className="border px-4 py-2">4 or more total credits from program courses</td>
              </tr>
              <tr>
                <td className="border px-4 py-2 font-semibold">Upper Level</td>
                <td className="border px-4 py-2">At least 1 course must be a Level 3 or Level 4 course</td>
              </tr>
              <tr>
                <td className="border px-4 py-2 font-semibold">Passing</td>
                <td className="border px-4 py-2">Student must pass and receive credit for all courses</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <p className="bg-blue-50 p-4 rounded border border-blue-200">
          <strong>Note:</strong> For a student to be counted as a CTE Completer (Code 7), they must meet 
          ALL of the above requirements within the SAME program of study. Courses and credits from 
          different programs of study do not combine for completer status.
        </p>
      </div>
      
      <div className="border-t pt-6">
        <p className="text-sm text-gray-600">
          This FAQ is based on the Texas Education Agency (TEA) CTE Indicator Auto-Code documentation.
          For more detailed information, please refer to the official TEA guidance or contact your
          regional Education Service Center.
        </p>
      </div>
    </div>
  );
}

export default FAQ;